import { FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusFirstInvalid } from '@/shared/hooks/use-focus-first-invalid';
import { useNotify } from '@/shared/hooks/use-notify';
import { useCreateFolder, useRefreshFolders, useUpdateFolder } from './api';
import {
  checkFolderName,
  folderErrorMessage,
  isNameTaken,
  isStaleVersion,
  isStepUpRequired,
  type Folder,
} from './model';

const useStyles = __css({"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"where":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"}});

export type NameDialogTarget =
  /** A new folder in `parent` (null: at the root of the project). */
  { kind: 'create'; parent: Folder | null } | { kind: 'rename'; folder: Folder };

export interface NameDialogProps {
  spaceId: string;
  target: NameDialogTarget;
  onClose: () => void;
  /** After a creation: the new folder (the tab opens it). */
  onCreated?: (folder: Folder) => void;
}

/** Creates a folder or renames one (If-Match on the folder's version). */
export function NameDialog({ spaceId, target, onClose, onCreated }: NameDialogProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const create = useCreateFolder();
  const update = useUpdateFolder(spaceId);
  const refresh = useRefreshFolders(spaceId);
  const { formRef, focusFirstInvalid } = useFocusFirstInvalid();

  const renaming = target.kind === 'rename';
  const [name, setName] = useState(renaming ? target.folder.name : '');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    setFailure(null);
    const checked = checkFolderName(name);
    if (!checked.ok) {
      setFieldError(t(`name.${checked.message}`));
      focusFirstInvalid();
      return;
    }
    setPending(true);
    try {
      if (target.kind === 'rename') {
        if (checked.name !== target.folder.name) {
          await update.mutateAsync({
            folderId: target.folder.id,
            version: target.folder.version,
            data: { name: checked.name },
          });
          notify({
            intent: 'success',
            message: t('rename.done', { name: isolate(checked.name) }),
          });
          await refresh();
        }
        onClose();
      } else {
        const created = await create.mutateAsync({
          spaceId,
          data: { parentId: target.parent?.id ?? null, name: checked.name },
        });
        notify({ intent: 'success', message: t('create.done', { name: isolate(checked.name) }) });
        await refresh();
        onCreated?.(created);
        onClose();
      }
    } catch (error) {
      if (isStepUpRequired(error)) return; // the app's step-up dialog replays the request
      if (isNameTaken(error)) {
        setFieldError(t('errors.nameTaken'));
        focusFirstInvalid();
      } else if (isStaleVersion(error)) {
        notify({ intent: 'warning', message: t('errors.stale') });
        await refresh();
        onClose();
      } else {
        setFailure(folderErrorMessage(t, error));
      }
    } finally {
      setPending(false);
    }
  };

  const where =
    target.kind === 'create'
      ? target.parent
        ? t('create.inFolder', { name: isolate(target.parent.name) })
        : t('create.inRoot')
      : null;

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !pending) onClose();
      }}
    >
      <DialogSurface>
        <form
          ref={formRef}
          noValidate
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          <DialogBody>
            <DialogTitle>{renaming ? t('rename.title') : t('create.title')}</DialogTitle>
            <DialogContent>
              <div className={styles.form}>
                {where ? <p className={styles.where}>{where}</p> : null}
                {failure ? (
                  <MessageBar intent="error" layout="multiline">
                    <MessageBarBody>{failure}</MessageBarBody>
                  </MessageBar>
                ) : null}
                <FormField
                  required
                  label={renaming ? t('rename.nameLabel') : t('create.nameLabel')}
                  error={fieldError}
                >
                  <Input
                    autoComplete="off"
                    value={name}
                    onChange={(_event, data) => {
                      setName(data.value);
                      setFieldError(undefined);
                    }}
                  />
                </FormField>
              </div>
            </DialogContent>
            <DialogActions>
              <Button type="button" appearance="secondary" onClick={onClose}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" appearance="primary" disabledFocusable={pending}>
                {pending
                  ? renaming
                    ? t('rename.saving')
                    : t('create.saving')
                  : renaming
                    ? t('rename.submit')
                    : t('create.submit')}
              </Button>
            </DialogActions>
          </DialogBody>
        </form>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/name-dialog.griffel.css";